import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { CheckCircle, Save } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function ReflectionAnswerForm({ reflectionId }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reflection, setReflection] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadReflection();
  }, [reflectionId]);

  const loadReflection = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/client/weekly-reflections/${reflectionId}`);
      setReflection(response.data);

      // If there's an existing response, populate answers
      if (response.data.response) {
        const answerMap = {};
        response.data.response.responses.forEach(r => {
          answerMap[r.questionId] = r.answer;
        });
        setAnswers(answerMap);
        setSubmitted(true);
      } else {
        // Initialize empty answers
        const initialAnswers = {};
        response.data.questions.forEach(q => {
          initialAnswers[q.id] = '';
        });
        setAnswers(initialAnswers);
      }
    } catch (err) {
      setError('Failed to load reflection');
      console.error('Error loading reflection:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if all questions are answered
    const unanswered = reflection.questions.filter(q => !answers[q.id]?.trim());
    if (unanswered.length > 0) {
      toast(`Please answer all questions before submitting. ${unanswered.length} question(s) remaining.`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const responses = reflection.questions.map(q => ({
        questionId: q.id,
        question: q.question,
        answer: answers[q.id]
      }));

      await axios.post(`${API_URL}/client/weekly-reflections/${reflectionId}/responses`, {
        responses
      });

      setSubmitted(true);
      toast.success('Your reflection has been submitted! Your coach will review your responses.');
    } catch (err) {
      setError('Failed to submit reflection');
      console.error('Error submitting reflection:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[var(--accent)]/30 border-t-[var(--accent)] mx-auto mb-4"></div>
          <p className="text-gray-400">Loading your reflection...</p>
        </div>
      </div>
    );
  }

  if (!reflection) {
    return (
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-8 text-center">
        <p className="text-gray-400">Reflection not found</p>
      </div>
    );
  }

  const progress = reflection.questions.filter(q => answers[q.id]?.trim()).length;
  const total = reflection.questions.length;
  const progressPercent = (progress / total) * 100;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
        <h2 className="text-2xl font-bold text-white mb-2">Your Weekly Reflection</h2>
        <p className="text-gray-400">
          Week of {new Date(reflection.week_start_date).toLocaleDateString()} - {new Date(reflection.week_end_date).toLocaleDateString()}
        </p>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm text-gray-400 mb-2">
            <span>Progress</span>
            <span>{progress} of {total} questions answered</span>
          </div>
          <div className="w-full bg-[var(--bg-primary)] rounded-full h-2">
            <div
              className="bg-[var(--accent)] h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Reflection Summary */}
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Reflection from Your Coach</h3>
        <div className="prose prose-invert max-w-none">
          <div className="text-gray-200 whitespace-pre-wrap leading-relaxed">
            {reflection.ai_summary}
          </div>
        </div>

        {reflection.coach_notes && (
          <div className="mt-6 p-4 bg-[var(--accent)]/10 border-l-4 border-[var(--accent)] rounded">
            <p className="text-sm font-semibold text-[var(--accent)] mb-2">Personal note from your coach:</p>
            <p className="text-gray-200">{reflection.coach_notes}</p>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      {submitted && (
        <div className="bg-green-500/10 border border-green-500/50 rounded-lg p-4 text-green-400 flex items-center gap-3">
          <CheckCircle className="w-5 h-5" />
          <span>Your reflection has been submitted. You can still edit your answers below.</span>
        </div>
      )}

      {/* Questions Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {reflection.questions.map((question, index) => (
          <div key={question.id} className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
            <label className="block mb-4">
              <div className="flex items-start gap-3 mb-3">
                <span className="flex-shrink-0 w-8 h-8 bg-[var(--accent)] text-white rounded-full flex items-center justify-center font-semibold">
                  {index + 1}
                </span>
                <span className="text-lg font-medium text-white flex-1 pt-0.5">
                  {question.question}
                </span>
              </div>
              <textarea
                value={answers[question.id] || ''}
                onChange={(e) => setAnswers({ ...answers, [question.id]: e.target.value })}
                placeholder={question.placeholder || 'Write your answer here...'}
                rows={6}
                className="w-full bg-[var(--bg-primary)] text-white px-4 py-3 rounded-lg border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] resize-none placeholder-gray-500"
              />
            </label>
          </div>
        ))}

        {/* Submit Button */}
        <div className="flex items-center justify-center">
          <button
            type="submit"
            disabled={submitting}
            className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white px-12 py-4 rounded-lg font-semibold text-lg transition-all flex items-center gap-3 disabled:opacity-50 shadow-lg"
          >
            <Save className="w-6 h-6" />
            {submitting ? 'Submitting...' : submitted ? 'Update My Answers' : 'Submit My Reflection'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ReflectionAnswerForm;
